export interface VersionInfo {
  id: number;
  parentId: number | null;
}

export interface ProcessVersionInfo {
  id: number | string;
  parentId: number | string | null;
}

export function processVersions(versions: Array<ProcessVersionInfo>): {
  [versionId in number | string]: boolean;
} {
  // Liveness analysis to see what versions are no longer present.
  // Keep track of the last version that was not reverted.
  let livingParentId: number | string | null;
  const revertedInfo: {[versionId in number | string]: boolean} = {};
  const lastVersion = versions[versions.length - 1];

  if (lastVersion != null) {
    livingParentId = lastVersion.id;
  } else {
    livingParentId = null;
  }

  for (let i = versions.length - 1; i >= 0; i--) {
    const version = versions[i];
    const isReverted = livingParentId !== version.id;
    if (!isReverted) {
      livingParentId = version.parentId;
    }
    revertedInfo[version.id] = isReverted;
  }
  return revertedInfo;
}

/**
 * Returns the version id we should reset to for an undo.
 * Returns null if we can't undo this version.
 *
 * Situation where we don't allow an undo:
 *  1. This is an original version, or the parent version can't be found.
 *  2. This is a revert of another version, and it does not have a grandparent version.
 *  This can happen when you revert back to the original version.
 */
export function getVersionIdToResetTo(
  versionToUndo: VersionInfo,
  versions: Array<VersionInfo>
): number | null {
  const parentVersion = versions.filter((v) => v.id === versionToUndo.parentId)[0];
  // Is original version or we can't locate version w/ that id.
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (versionToUndo.parentId === null || parentVersion === null) {
    return null;
  }

  // eslint-disable-next-line no-restricted-syntax
  const sortedIds = versions.map((v) => v.id).sort();
  // We already checked that it has a parent, so there should be at least one
  // lower id in the set.
  const nextLowestId = sortedIds[sortedIds.indexOf(versionToUndo.id) - 1];

  if (versionToUndo.parentId === nextLowestId) {
    // This is not a revert version so we just revert
    // to its parent.
    return versionToUndo.parentId;
  } else {
    // This is a revert version so we should revert to the
    // grandparent, since the parent is identical to this version.
    // If this is a revert back to the original version, this will
    // return null.
    return parentVersion.parentId;
  }
}

export function hasReverts(versions: Array<VersionInfo>): boolean {
  const sortedById = versions.sort((v1, v2) => v1.id - v2.id);
  for (let i = 1; i < sortedById.length; i++) {
    if (sortedById[i].parentId !== sortedById[i - 1].id) {
      return true;
    }
  }
  return false;
}

export function isRevert(versions: Array<VersionInfo>, target: VersionInfo): boolean {
  // Version IDs are monotonically increasing, so sorting by them will correctly order versions
  // eslint-disable-next-line no-restricted-syntax
  const sortedIds = versions.map((v) => v.id).sort();
  const targetIdx = sortedIds.indexOf(target.id);

  // The original version is obviously not a revert
  if (targetIdx === 0) {
    return false;
  }

  // If a version's parentId is not the ID of the immediately preceding version, it's a revert
  return target.parentId !== sortedIds[targetIdx - 1];
}
